<div class="container-fluid">
  <hr>
  <!-- comments section -->
  <div class="position-relative">
    <h6 class="text-black text-opacity-50 mt-4 d-flex align-items-center justify-content-between"
        [ngClass]="{'clickable': commentsCollapsed}"
        (click)="commentsCollapsed ? toggleExpandComments($event) : false">
      <div class="d-flex align-items-center gap-2">
        <!--  Do not use comment_count field of the ticket if all the comments that can be loaded have been loaded, to
            ensure that if comments were added or removed on the server and the already loaded comments are out of sync
            with the server, show the count as the number of comments loaded. The user will need to reload to fetch new
            comments -->
        {{ 'Comments' | messageTranslate }}: {{ haveMoreCommentsToLoad() && ticket?.comment_count != null ? ticket.comment_count : (ticket.comments?.length || 0) }}
        <button type="button" class="btn btn-link shadow-none p-0" (click)="toggleActivity()">
          <fa-icon icon="history" class="text-secondary" [appShowFaSpinner]="loadingChangeHistory" [fixedWidth]="true" [transform]="{x: -2}" />{{(showChangeHistory ? 'Hide Activity' : 'Show Activity') | messageTranslate}}
        </button>
      </div>

      <!-- Show this button even if there are no comments, as after expanding the section it will be possible to load change history -->
      <button class="btn btn-sm btn-link p-0" type="button"
              [title]="(commentsCollapsed ? 'Expand comments' : 'Collapse comments') | messageTranslate"
              (click)="toggleExpandComments($event)">
        <fa-icon class="clickable"
                 [icon]="commentsCollapsed ? 'angle-double-down' : 'angle-double-up'"
                 [fixedWidth]="true"/>
      </button>
    </h6>
    @if (!readOnly) {
      <app-upsert-ticket-comment #commentComponent
                                 [rows]="2"
                                 [ticket]="ticket"
                                 [text]="savedCommentText"
                                 [grabFocus]="addCommentConfig?.showAddComment && !isMobile"
                                 [showErrors]="false"
                                 [includeTicketUpdateFields]="true"
                                 [showLabels]="false"
                                 [placeholder]="'Add a comment' | messageTranslate"
                                 [requireComment]="!hasCommentAttachments"
                                 [assigneesByEntityName]="assigneesByEntityName"
                                 [assigneesLoading]="assigneesLoading"
                                 (commentFocus)="onCommentBoxFocus($event)"
                                 (progressEvent)="onAddCommentProgress($event)"/>
      <app-upload-state [recordId]="ticket.ticket_id"
                        [disabled]="updating"
                        (attachmentUploading)="setCommentAttachmentUploading($event)"
                        (attachmentAssociated)="onAttachmentsAssociated($event)"
                        (hasAttachments)="setHasCommentAttachments($event)"
                        (attachmentOpen)="onCommentAttachmentOpen($event)"
                        (associatedAttachmentDelete)="deleteAssociatedAttachmentById($event)"/>
      <input #commentUploadButtonSelectDirective
             ngfSelect
             accept="*"
             multiple
             type="file"
             class="hide-file-input"
             [ngfFixOrientation]="false"
             (filesChange)="onCommentFilesChange($event)">
      @if (commentBoxFocused || commentComponent?.hasText() || hasCommentAttachments || mentionLastFocused) {
        <div class="row my-2">
          <div class="col d-flex gap-2">
            <app-button visual="primary"
                        label="Add comment"
                        [loading]="updating"
                        [disabled]="!commentComponent?.valid || commentAttachmentUploading"
                        (buttonClick)="submitComment()"/>
            <!-- Using (mousedown) instead of (click) because (mousedown) triggers as soon as button is pressed but (click) triggers after letting go.
                 These buttons are initially hidden and the only way to press them without comment is by clicking this button after focusing on comment box.
                 when using (click), its possible that the press lasts too long that the buttons would have disappeared already. -->
            <button type="button" class="btn btn-secondary ms-auto ms-md-0"
                    [disabled]="updating"
                    (mousedown)="commentUploadButtonSelectDirective.click()">
              <fa-icon icon="paperclip" class="me-1"/>
              {{ 'Attach' | messageTranslate }}
            </button>
            @if (isMobile) {
              <select class="form-select mention"
                      [formControl]="mobileMentionControl"
                      (focus)="updateMentionLastFocus(true)"
                      (blur)="updateMentionLastFocus(false)">
                <option [ngValue]="null" disabled hidden="hidden" selected>{{'Mention' | messageTranslate}}</option>
                @if (assigneesLoading) {
                  <option disabled>{{ 'Loading...' | messageTranslate}}</option>
                }
                @for (assigneesByEntityNameKeyValue of assigneesByEntityName | keyvalue; track assigneesByEntityNameKeyValue.key) {
                  <optgroup [label]="assigneesByEntityNameKeyValue.key">
                    @for (assignee of assigneesByEntityNameKeyValue.value; track assignee.id) {
                      <option [ngValue]="assignee">{{ assignee.fullName }}</option>
                    }
                  </optgroup>
                }
              </select>
            } @else {
              <div class="mention" ngbDropdown appLastClickInside (lastClickInsideChange)="updateMentionLastFocus($event)">
                <button type="button" class="btn btn-secondary" ngbDropdownToggle>
                  {{ 'Symbol Mention' | messageTranslate:{symbol: MENTION_CHARACTER} }}
                </button>
                <div ngbDropdownMenu>
                  @if (assigneesLoading) {
                    <div class="text-center">
                      <fa-icon [appShowFaSpinner]="true"/>
                    </div>
                  }
                  @for (assigneesByEntityNameKeyValue of assigneesByEntityName | keyvalue; track assigneesByEntityNameKeyValue.key) {
                    <div class="py-2 px-3 text-truncate text-body-secondary" ngbdropdownitem>{{assigneesByEntityNameKeyValue.key}}</div>
                    @for (assignee of assigneesByEntityNameKeyValue.value; track assignee.id) {
                      <button type="button" class="py-2 px-4 text-truncate" ngbDropdownItem
                              (click)="addMention(assignee)">
                        {{assignee.fullName}}
                      </button>
                    }
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }
    }

    @if (commentsCollapsed && ticket?.comments?.length) {
      <app-ticket-comment [site]="site"
                          [comment]="ticket.comments[0]"
                          [assigneesByEmail]="assigneesByEmail"
                          [allowEdit]="!readOnly"
                          (editComment)="onEditComment($event)" />
      <div class="mt-2">
        @if (ticket.comments.length > 1) {
          <button class="btn btn-link shadow-none px-0" (click)="expandComments($event)">
            <fa-icon class="clickable" [icon]="'angle-double-down'" [fixedWidth]="true" [transform]="{x: -2}"/>{{ticket.comments.length === 2
              ? ('1 more comment' | messageTranslate)
              : ('n more comments' | messageTranslate: {moreCommentCount: ticket.comments.length - 1})}}
          </button>
        }
      </div>
    }
    @if (!commentsCollapsed) {
      <div mwlResizable [enableGhostResize]="true" [ghostElementPositioning]="'absolute'" (resizeEnd)="onResizeEnd($event)">
        <div #activityContainer infiniteScroll [infiniteScrollDistance]="2" [infiniteScrollThrottle]="50" [scrollWindow]="false" (scrolled)="onInfiniteScroll()"
             class="container-fluid px-0" style="overflow-y: scroll; scroll-behavior: smooth;" (scroll)="activityScrolled()"
             [ngStyle]="{'max-height': !manuallySetHeight ? activityContainerHeight + 'px' : undefined, 'height': manuallySetHeight ? activityContainerHeight + 'px': undefined}">
          <app-view-ticket-activity [ticketType]="ticket.type"
                                    [site]="site"
                                    [equipmentLoading]="equipmentLoading"
                                    [equipmentById]="equipmentById"
                                    [comments]="ticket.comments"
                                    [allCommentsLoaded]="!haveMoreCommentsToLoad()"
                                    [changeHistory]="ticket.change_history"
                                    [allChangeHistoryLoaded]="!haveMoreChangeHistoryToLoad()"
                                    [showChangeHistory]="showChangeHistory"
                                    [assigneesByEmail]="assigneesByEmail"
                                    [readOnly]="readOnly"
                                    (editComment)="onEditComment($event)"/>
          @if (loadingActivity) {
            <div class="position-relative mt-3">
              <div class="spinner">
                <div class="bounce1"></div>
                <div class="bounce2"></div>
                <div class="bounce3"></div>
              </div>
            </div>
          }
        </div>
        <div class="d-flex mt-3">
          @if (doesActivityScrollbarExist || haveMoreActivityToLoad()) {
            <div class="me-2">
              <fa-layers [fixedWidth]="true" [title]="(scrollDownForActivity() ? 'More' : 'Scroll to Top') | messageTranslate"
                         class="clickable" (click)="scrollActivity(scrollDownForActivity() ? 'down' : 'up')">
                <fa-icon [icon]="['fas', 'circle']" class="text-secondary"/>
                <fa-icon [icon]="scrollDownForActivity() ? 'chevron-down' : 'chevron-up'" class="text-white" [transform]="'shrink-4'"/>
              </fa-layers>
            </div>
          }
          @if (ticket?.comments?.length || (showChangeHistory && ticket?.change_history?.length)) {
            <div mwlResizeHandle class="resize-handle my-auto me-2"
                 [resizeEdges]="{ bottom: true }"></div>
          }
          @if (manuallySetHeight && (ticket?.comments?.length || (showChangeHistory && ticket?.change_history?.length))) {
            <div>
              <fa-icon [icon]="['far', 'times-circle']" class="text-secondary clickable" [title]="'Reset' | messageTranslate"
                       (click)="resetActivityHeight()"/>
            </div>
          }
        </div>
      </div>
    }
  </div>
  <!-- end of comments section -->
  <hr>

  <div class="position-relative">
    <button class="btn btn-sm btn-link p-0 position-absolute" style="top: 0; right: 0;" type="button"
            [title]="fieldsCollapsed ? 'Expand ticket fields' : 'Collapse ticket fields' | messageTranslate"
            (click)="toggleExpandTicketFields()">
      <fa-icon [icon]="fieldsCollapsed ? 'angle-double-down' : 'angle-double-up'" [fixedWidth]="true" class="clickable"/>
    </button>

    @if (ticket?.equipment_ids?.length) {
      <h6 class="text-black text-opacity-50 mt-4 mb-0">{{ 'Equipment' | messageTranslate }}</h6>
      <div class="row">
        <div class="col">
          <div class="text-body-secondary">
            @if (equipmentLoading) {
              <span>
                {{ 'Loading...' | messageTranslate }}
              </span>
            } @else {
              @for (equipmentId of ticket.equipment_ids; track equipmentId) {
                @if (equipmentById?.get(equipmentId) || { name: equipmentId }; as equipment) {
                  @let equipmentType = equipment.metadata_type_id != null ? metadataTypesById?.get(equipment.metadata_type_id)?.type : null;
                  @let levelName = equipment.zone?.level?.level_name;
                  @let zoneName = equipment.zone?.zone_name?.zone_name;
                  @let levelZone = (levelName && zoneName) ? (levelName + ', ') + zoneName : '';
                  @let separator = (equipmentType && levelZone) ? ' - ' : '';
                  <span class="badge rounded-pill text-bg-secondary me-1"
                        [attr.title]="((equipmentType ?? '') + separator + levelZone) || undefined">
                    {{ equipment.name }}
                  </span>
                }
              }
            }
          </div>
        </div>
      </div>
    }

    @if (ticket?.description) {
      <h6 class="text-black text-opacity-50 mt-4 mb-0">{{ 'Description' | messageTranslate }}</h6>
      <div class="row">
        <div class="col">
          @if (fieldsCollapsed) {
            <div class="text-truncate clickable" (click)="expandTicketFields($event)">
              <span [innerHTML]="collapsedFields.collapsedDescription | linkify"></span>
            </div>
          } @else {
            <div class="text-break text-prewrap"><span [innerHTML]="ticket?.description | linkify"></span></div>
          }
        </div>
      </div>
    }

    @if (ticket?.solution) {
      <h6 class="text-black text-opacity-50 mt-4 mb-0">{{ 'Solution' | messageTranslate  }}</h6>
      <div class="row">
        <div class="col">
          @if (fieldsCollapsed) {
            <div class="text-truncate clickable" (click)="expandTicketFields($event)">
              <span [innerHTML]="collapsedFields.collapsedSolution | linkify"></span>
            </div>
          } @else {
            <div class="text-break text-prewrap"><span [innerHTML]="ticket?.solution | linkify"></span></div>
          }
        </div>
      </div>
    }

    @let hasSavings = ticket.savings > 0;
    @if (showTimeTaken || hasSavings) {
      <div class="row mt-4">
        @if (showTimeTaken) {
          <div class="col form-group">
            <h6 class="text-black text-opacity-50 mt-4 mb-0">{{ 'Time Spent' | messageTranslate }}</h6>
            @if (ticket.time_taken) {
              {{ticket.time_taken | duration:{units: ['h', 'm']} }}
            } @else {
              {{'None added' | messageTranslate}}
            }
            @if (!readOnly) {
              <fa-icon icon="pencil-alt"
                       class="text-body-secondary clickable small"
                       [title]="(ticket.time_taken ? 'Update Time Spent' : 'Record Time Spent') | messageTranslate"
                       [fixedWidth]="true"
                       (click)="upsertTimeTaken()"/>
            }
          </div>
        }
        @if (hasSavings) {
          <div class="col form-group">
            <h6 class="text-black text-opacity-50 mt-4 mb-0">{{ 'Cost Savings' | messageTranslate }}</h6>
            <app-ace-currency [value]="ticket.savings" [currency]="site.monetary_currency"/>
          </div>
        }
      </div>
    }

    <h6 class="text-black text-opacity-50 mt-4 mb-0" [ngClass]="{'clickable': fieldsCollapsed}" (click)="fieldsCollapsed ? expandTicketFields($event) : false">
      {{ 'Attachments' | messageTranslate }}: {{ ticket.attachments?.length ?? 0 }}
    </h6>
    <!-- using `d-none` for hiding `AttachmentsComponent` because clicking on comment attachments uses `AttachmentsComponent` so it needs to exist all the time.
     Also ongoing uploads will get cancelled if `AttachmentsComponent` disappears -->
    <app-attachments
      [ngClass]="{'d-none': fieldsCollapsed}"
      [attachments]="ticket.attachments"
      [recordId]="ticket.ticket_id"
      [attachmentIdToOpen]="showAttachmentId"
      [deleteAccess]="readOnly ? AttachmentDeleteAccess.NOT_ALLOWED : attachmentDeleteAccess"
      [addAllowed]="!readOnly && attachmentAddAllowed"
      (copyLink)="copyToClipboard($event)"
      (attachmentAssociated)="onAttachmentsAssociated($event)"
      (attachmentsFetched)="onAttachmentsFetched($event)"
      (attachmentDeleted)="deleteAttachment($event)"
      (attachmentPreviewUserAction)="onAttachmentPreviewUserAction($event)"
      (attachmentUploading)="setAttachmentUploading($event)"/>

    <div class="row mt-4">
      @if (ticket.benefit && ticket.benefit.length) {
        <div class="col form-group">
          <h6 class="text-black text-opacity-50 mb-0">{{ 'Impact' | messageTranslate  }}</h6>
          <div class="badge rounded-pill text-bg-info-gray">
            {{ ticket.benefit[0].benefit_name }}
          </div>
        </div>
      }
      <div class="col form-group">
        <h6 class="text-black text-opacity-50 mb-0">{{ 'Priority' | messageTranslate }}</h6>
        <app-priority-badge [priority]="ticket?.priority" [rounded]="true" />
      </div>
      <!-- If all columns are shown, force next columns to break to new line at breakpoint below sm -->
      @if ((resolvedAt || updatedAt) && ticket.benefit && ticket.benefit.length && ticket.priority) {
        <div class="w-100 d-sm-none"></div>
      }
      @if (resolvedAt) {
        <div class="col form-group">
          <h6 class="text-black text-opacity-50 mb-0">{{'Resolved' | messageTranslate}}</h6>
          <div>{{resolvedAt}}</div>
        </div>
      } @else {
        <div class="col form-group">
          <h6 class="text-black text-opacity-50 mb-0">{{'Updated' | messageTranslate}}</h6>
          <div>{{updatedAt}}</div>
        </div>
      }
      <div class="col form-group">
        <h6 class="text-black text-opacity-50 mb-0">{{'Created' | messageTranslate}}</h6>
        <div>{{createdAt}}</div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col form-group">
        <h6 class="text-black text-opacity-50 ">{{ 'Linked Alerts' | messageTranslate }}</h6>
        <div class="ticket-select-container">
          <app-ticket-select #linkedAlertsSelect
                             [site]="site"
                             [ticketType]="TicketType.ALERT"
                             [saving]="isActionExecuting(ExecutableAction.LINK_TICKET)"
                             [formControl]="linkedAlertsControl"
                             [multiSelect]="true"
                             [allowToOpenTicketView]="showViewLinkedTicket"
                             [canChangeEditMode]="!readOnly && !isActionInProgress()"
                             (viewTicket)="viewLinkedAlert($event)"
                             (saveEvent)="saveLinkedAlerts()"
                             (cancelEvent)="cancelEditLinkedAlerts()"/>
        </div>
      </div>
    </div>
  </div>

  <hr>

  <div class="row mt-4">
    <div class="col d-flex flex-wrap gap-2">
      @if (!readOnly && isActionAvailable(ExecutableAction.EDIT)) {
        <app-button visual="secondary" label="Edit" [disabled]="isActionInProgress()" (buttonClick)="editTicket()"/>
      }
      @if (isActionAvailable(ExecutableAction.EMAIL)) {
        <app-button visual="secondary"
                    label="Forward"
                    [loading]="isActionExecuting(ExecutableAction.EMAIL)"
                    [disabled]="isActionInProgress()"
                    (buttonClick)="email()"/>
      }
      <a class="btn btn-secondary" target="_blank" rel="opener" [href]="exportTicketLink">
        {{ 'Export' | messageTranslate }}</a>
    </div>
  </div>
</div>
